/* @flow */
import React, { Component, useRef, useState } from "react";
import styled, { css } from "styled-components";
import StockMenu from "../StockMenu";
import Fade from "../common/Animations/Fade";
import Tour from "../Tour";

const Container = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  display: inline-block;
  vertical-align: top;
  height: 100%;
  z-index: 102;
`;

const StockContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
  margin-right: 22px;
`;

const Stock = styled.button`
  position: relative;
  display: inline-block;
  vertical-align: middle;
  height: 100%;
  margin-left: 25px;
  padding: 0;
  padding-right: 17px;
  font-size: 24px;
  font-weight: 400;
  color: ${({ theme }) => theme.foreground};
  line-height: 1;
  text-transform: uppercase;
  border: none;
  background-color: ${props => props.theme.main};
  cursor: pointer;
  
  ${({ active }) =>
    active &&
    css`
      z-index: 103;
    `};

  @media (max-width: 959px) {
    z-index: 1;
  }

  ${props =>
    props.active &&
    css`
      background-color: ${props => props.theme.popupBackground};
    `};

  @media (max-width: 959px) {
    margin-left: 0;
    margin-right: 0;
    padding: 4px;
    padding-right: 21px;
    font-size: 16px;
    background-color: ${({ theme }) => theme.mobileSwitchStockBackground};
    border-radius: 4px;
  }

  @media (max-width: 425px) {
    font-size: 13px;
    padding-right: 18px;
  }

  ${({ isTour }) =>
    isTour &&
    css`
      margin-left: 0 !important;
      background-color: initial !important;
    `};

  :after {
    ${({ tourBorder }) =>
      tourBorder &&
      css`
        position: absolute;
        left: -10px;
        top: -10px;
        right: -10px;
        bottom: -10px;
        border-radius: 6px;
        ${tourBorder.border};
        content: "";
      `};

    ${({ tourBorderAnimation }) =>
      tourBorderAnimation &&
      tourBorderAnimation.animation(tourBorderAnimation.keyframe)};
  }
`;

const StockArrow = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%) rotate(0deg);
  transform-origin: 50%;
  border: 5px solid transparent;
  border-top-color: #99959d;
  border-bottom: none;
  transition: all 0.3s ease;

  ${props =>
    props.active &&
    css`
      transform: translateY(-50%) rotate(-180deg);
      transform-origin: 50%;
    `};

  @media (max-width: 959px) {
    right: 4px;
    border-top-color: ${({ theme }) => theme.mobileSwitchStockArrow};
  }
`;

const Menu = styled.div`
  display: inline-block;
  height: 450px;
  width: 100%;
  padding-top: 53px;
  padding-left: 28px;
  padding-right: 13px;
  padding-bottom: 13px;
  background-color: ${props => props.theme.popupBackground};
  z-index: 1;

  :before {
    content: "";
    position: absolute;
    top: 53px;
    left: 28px;
    width: calc(100% - 41px);
    border-top: 1px solid ${props => props.theme.mainSeparator};
  }
`;

const Otc = styled.span`
  display: inline-block;
  vertical-align: top;
  margin-left: 7px;
  margin-right: 4px;
  padding: 4px;
  font-size: 16px;
  line-height: 1;
  font-weight: 700;
  color: ${props => props.theme.main};
  background-color: ${({ theme }) => theme.accent};
  border-radius: 2px;

  @media (max-width: 959px) {
    vertical-align: text-top;
    padding: 2px;
    font-size: ${({ theme }) =>
      theme.locale === "ar" ? theme.minFontSize : "14px"};
  }
`;

type ToolProps = {
  value: string,
  active?: boolean,
  onClick?: () => void
};

export const StockInTools = (props: ToolProps) => {
  const original = useRef(null);
  const [isClicked, click] = useState(false);

  return (
    <Tour step="showStockSelect" originalRef={original}>
      {({ next, isOriginal, isTourActive, tour }) => (
        <Stock
          tourBorder={!isOriginal && tour && tour.borderStyles}
          tourBorderAnimation={
            !isOriginal && tour && tour.outside && tour.borderStyles
          }
          onClick={() => {
            setTimeout(next, 0);
            if (isTourActive) {
              click(true);
            }
            if (!isClicked) {
              if (typeof props.onClick === "function") {
                props.onClick();
              }
            }
          }}
          active={props.active}
          isTour={!isOriginal}
          ref={isOriginal ? original : null}
        >
          {props.value.replace("_OTC", "").slice(0, 6)}
          {props.value.endsWith("_OTC") && <Otc>OTC</Otc>}
          <StockArrow active={props.active} />
        </Stock>
      )}
    </Tour>
  );
};

type Props = {
  value: string,
  tabs: Array<string>,
  onNextActiveTab?: () => void,
  onChange: (nextValue: string) => void
};

type State = {
  isActive: boolean
};

class SwitchStock extends Component<Props, State> {
  state = {
    isActive: false
  };

  toggle = () => {
    this.setState({
      isActive: !this.state.isActive
    });
  };

  hide = e => {
    if (
      e &&
      typeof e.preventDefault === "function" &&
      typeof e.stopPropagation === "function"
    ) {
      e.preventDefault();
      e.stopPropagation();
    }

    this.setState({
      isActive: false
    });
  };

  handleStockChange = (stock: string) => {
    const tabIndex = this.props.tabs.indexOf(stock);

    this.hide();
    if (tabIndex === -1) {
      this.props.onChange(stock);
    } else {
      this.props.onNextActiveTab(tabIndex);
    }
  };

  original = React.createRef(null);

  render() {
    return (
      <StockContainer>
        <StockInTools
          onClick={this.toggle}
          active={this.state.isActive}
          value={this.props.value}
        />
        <Fade in={this.state.isActive}>
          {({ style }) => (
            <Container style={style}>
              <Tour
                step={["stockSelect", "allStocksSelect"]}
                disabled
                originalRef={this.original}
              >
                {({ next, isOriginal, isTourActive, step }) => (
                  <Menu ref={isOriginal ? this.original : null}>
                    <StockMenu
                      isTour={!isOriginal}
                      defaultCategory={
                        isTourActive && step === "stockSelect"
                          ? "popular"
                          : null
                      }
                      disabledOutside={isOriginal && isTourActive}
                      onStockSelect={a => {
                        step !== "stockSelect" && next(a);
                        this.handleStockChange(a);
                      }}
                      onClose={isOriginal ? this.hide : null}
                    />
                  </Menu>
                )}
              </Tour>
            </Container>
          )}
        </Fade>
      </StockContainer>
    );
  }
}

export default SwitchStock;



// WEBPACK FOOTER //
// ./src/components/Toolbar/SwitchStock.js